import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Portal } from './Portal';

interface MultiStatusSelectProps {
  selectedStatuses: string[];
  onChange: (statuses: string[]) => void;
}

// Map of database values to translation keys
const STATUS_TRANSLATION_MAP: Record<string, string> = {
  'No se ha presentado': 'meetingStatus.notPresented',
  'No nos hemos presentado': 'meetingStatus.weDidNotPresent',
  'Positiva - Seguimiento': 'meetingStatus.positiveFollowUp',
  'Positiva - Cerrado': 'meetingStatus.positiveClosed',
  'Negativa': 'meetingStatus.negative',
  'Negativa - Perfil inadecuado': 'meetingStatus.negativeInadequateProfile',
  'Sin respuesta': 'meetingStatus.noResponse',
  'Cancelada': 'meetingStatus.cancelled'
};

export const STATUS_OPTIONS = [
  { value: 'No se ha presentado', color: 'bg-blue-200 text-blue-900' },
  { value: 'No nos hemos presentado', color: 'bg-blue-100 text-blue-800' },
  { value: 'Positiva - Seguimiento', color: 'bg-green-100 text-green-800' },
  { value: 'Positiva - Cerrado', color: 'bg-green-700 text-green-100' },
  { value: 'Negativa', color: 'bg-red-200 text-red-800' },
  { value: 'Negativa - Perfil inadecuado', color: 'bg-red-700 text-red-100' },
  { value: 'Sin respuesta', color: 'bg-gray-300 text-gray-800' },
  { value: 'Cancelada', color: 'bg-gray-800 text-gray-100' }
];

export const MultiStatusSelect: React.FC<MultiStatusSelectProps> = ({
  selectedStatuses,
  onChange,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to get translated label for a status value
  const getStatusLabel = (statusValue: string): string => {
    const translationKey = STATUS_TRANSLATION_MAP[statusValue];
    return translationKey ? t(translationKey) : statusValue;
  };

  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 300; // min-w-[300px]
      const margin = 16; // Right margin to prevent cutoff

      // Calculate left position, ensuring dropdown doesn't go off-screen
      let left = rect.left;

      // If dropdown would extend beyond viewport, align it to the right instead
      if (left + dropdownWidth > window.innerWidth - margin) {
        left = window.innerWidth - dropdownWidth - margin;
      }

      // Ensure left is never negative (for very small screens)
      left = Math.max(margin, left);

      setDropdownStyle({
        top: rect.bottom + 4,
        left: left,
        width: rect.width
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        dropdownRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleStatus = (status: string) => {
    if (selectedStatuses.includes(status)) {
      onChange(selectedStatuses.filter(s => s !== status));
    } else {
      onChange([...selectedStatuses, status]);
    }
  };

  const selectOnly = (status: string) => {
    onChange([status]);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          if (!isOpen) {
            updatePosition();
          }
          setIsOpen(!isOpen);
        }}
        className={`inline-flex items-center space-x-2 px-3 py-1.5 text-sm font-medium ${
          selectedStatuses.length > 0
            ? 'text-[#0055FF] border-[#0055FF] bg-blue-50'
            : 'text-gray-700 border-gray-300 bg-white'
        } border hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#0055FF]`}
      >
        <div className="flex items-center space-x-2">
          <span>{t('meetings.statusFilter')}</span>
          {selectedStatuses.length > 0 && (
            <span className="flex items-center text-xs bg-blue-50 text-[#0055FF] px-1.5">
              {selectedStatuses.length}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <Portal>
          <div
            ref={dropdownRef}
            className="fixed z-[1000] bg-white shadow-lg border border-gray-200 py-2 min-w-[300px]"
            style={{
              top: `${dropdownStyle.top}px`,
              left: `${dropdownStyle.left}px`
            }}
          >
            {selectedStatuses.length > 0 && (
              <div className="px-3 pb-2 border-b border-gray-200">
                <button
                  onClick={() => {
                    onChange([]);
                    setIsOpen(false);
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  {t('meetings.clearFilters')}
                </button>
              </div>
            )}
            {STATUS_OPTIONS.map((status) => (
              <div
                key={status.value}
                className="px-3 py-2 hover:bg-gray-50"
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedStatuses.includes(status.value)}
                    onChange={() => {
                      toggleStatus(status.value);
                    }}
                    className="h-4 w-4 text-[#0055FF] border-gray-300 focus:ring-[#0055FF]"
                  />
                  <span
                    className={`ml-2 flex-1 text-sm px-2 py-0.5 ${status.color}`}
                    onClick={() => {
                      toggleStatus(status.value);
                    }}
                  >
                    {getStatusLabel(status.value)}
                  </span>
                  <button
                    onClick={() => selectOnly(status.value)}
                    className="ml-2 text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
                  >
                    {t('meetings.only')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Portal>
      )}
    </div>
  );
};